import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

import { KeycloakStateService } from "src/app/core/service/utility.service";
import { SmartContractAccessFlowService } from "../smart-contract-access-flow.service";
import { SuccessDialogComponent } from "../../shared/dialogs/success-dialog/success-dialog.component";
import { ErrorDialogComponent } from "src/app/features/ada-access/shared/dialogs/error-dialog.component";
import { Header } from "@aptsi-types";

@Component({
  selector: "app-missing-access-flow-dialog",
  templateUrl: "./missing-access-flow-dialog.component.html",
  styleUrls: ["./missing-access-flow-dialog.component.scss"]
})
export class MissingAccessFlowDialogComponent implements OnInit {

  //──────────────── Pagination ────────────────
  currentPage = 1;
  itemsPerPage = 10;

  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  //──────────────── Search ────────────────
  filterByHeader = "assetName";
  searchText = "";
  searchTextChanged = new Subject<string>();
  isSearch = false;

  //──────────────── Data ────────────────
  filteredData: any[] = [];
  searchedData: any[] = [];

  organizationId: string;
  loading = false;

  //──────────────── Selection ────────────────
  selectedEntries: any[] = [];
  selectedAssetIds = new Set<string>();

  headers: Header[] = [
    {
      name: "Asset Name",
      property: "assetName",
      showInDropdown: true
    },
    {
      name: "Principal Type",
      property: "principalTypeName",
      showInDropdown: true
    }
  ];

  constructor(
    private service: SmartContractAccessFlowService,
    private keycloak: KeycloakStateService,
    private dialog: MatDialog,
    private router: Router
  ) { }

  async ngOnInit(): Promise<void> {

    this.organizationId = this.keycloak.getOrganizationId();

    await this.fetchAssets();

    this.searchTextChanged
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.searchAssets();
      });
  }

  //──────────────── Fetch Normal ────────────────

  async fetchAssets(): Promise<void> {

    try {

      this.loading = true;

      const response = await this.service
        .getRemainingAssetsAccessFlow(
          this.currentPage - 1,
          this.organizationId
        )
        .toPromise();

      this.filteredData = response.map(asset => ({
        ...asset
      }));

      this.totalPagesNormal =
        response[0]?.totalPages || 0;

      this.totalPages =
        this.totalPagesNormal;

      const data = (response || []).map((x: any) => ({
        ...x,
        principalTypeName: x.principalTypeName || x.principalTypeId
      }));

      this.filteredData = data;

      this.totalPagesNormal = Math.ceil(
        data.length / this.itemsPerPage
      );

      this.totalPages = this.totalPagesNormal;

    } catch (err) {

      console.error(err);

    } finally {

      this.loading = false;
    }
  }

  //──────────────── Search ────────────────

  async fetchAssetSearch(): Promise<void> {

    try {

      this.loading = true;

      const response = await this.service
        .getRemainingAssetsAccessFlowSearch(
          this.currentPage - 1,
          this.searchText,
          this.filterByHeader,
          this.organizationId
        )
        .toPromise();


      this.searchedData = response.map(asset => ({
        ...asset
      }));

      this.totalPagesSearch =
        response[0]?.totalPages || 0;

      this.totalPages =
        this.totalPagesSearch;

    } catch (err) {

      console.error(err);

    } finally {

      this.loading = false;
    }
  }

  //──────────────── Pagination ────────────────

  changePage(page: number): void {

    this.currentPage = page;

    if (this.isSearch) {

      this.fetchAssetSearch();

    } else {

      this.fetchAssets();
    }
  }

  //──────────────── Selection ────────────────

  onSelectionChanged(selectedRows: any[]): void {

    const currentData = this.isSearch
      ? this.searchedData
      : this.filteredData;

    const currentIds = currentData.map(x => x.assetId);

    currentIds.forEach(id => this.selectedAssetIds.delete(id));

    selectedRows.forEach(asset => {
      this.selectedAssetIds.add(asset.assetId);
    });

    const allAssets = [
      ...this.filteredData,
      ...this.searchedData,
      ...this.selectedEntries
    ];

    const uniqueAssets = allAssets.filter(
      (asset, index, self) =>
        index === self.findIndex(
          x => x.assetId === asset.assetId
        )
    );

    this.selectedEntries = uniqueAssets.filter(
      asset => this.selectedAssetIds.has(asset.assetId)
    );
  }

  //──────────────── Search ────────────────

  onSearchTextChange(value: string): void {

    this.searchTextChanged.next(value);
  }

  async searchAssets(): Promise<void> {

    if (this.searchText.trim()) {

      this.isSearch = true;
      this.currentPage = 1;

      await this.fetchAssetSearch();

    } else {

      this.isSearch = false;
      this.currentPage = 1;

      await this.fetchAssets();
    }
  }

  //──────────────── Finish ────────────────

  finish(): void {

    if (!this.selectedEntries.length) {

      this.openErrorDialog("Please select at least one Asset");

      return;
    }

    const payload = this.selectedEntries.map(asset => ({
      organizationId: this.organizationId,
      assetId: asset.assetId,
      principalTypeId: asset.principalTypeId
    }));

    this.service
      .generateRemainingAssetsAccessFlow(payload)
      .subscribe(
        () => {

          this.close();

          this.openSuccessDialog(true);

        },
        error => {

          console.error(error);

          this.openErrorDialog(
            "Failed to create access flows"
          );
        }
      );
  }

  //──────────────── Dialogs ────────────────

  openSuccessDialog(isCreate: boolean): void {

    const dialogRef = this.dialog.open(
      SuccessDialogComponent,
      {
        width: "250px",
        data: {
          isCreate,
          title: "Access Flow"
        }
      }
    );

    dialogRef.afterClosed().subscribe(() => {

      this.router.navigateByUrl(
        "/",
        {
          skipLocationChange: true
        }
      ).then(() => {

        this.router.navigate([
          "/secure-blockchain/auth-tools/access-flow/search"
        ]);
      });

    });
  }

  openErrorDialog(message: string): void {

    this.dialog.open(
      ErrorDialogComponent,
      {
        width: "250px",
        data: {
          errorMessage: message
        }
      }
    );
  }

  close(): void {

    this.dialog.closeAll();
  }
}